import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Calendar, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Logo from "../components/Logo";

export default function RegistrationSuccessPage() {
  const { number } = useParams();
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/registrations/${number}`).then((r) => setReg(r.data)).catch(() => setErr("Inscription introuvable"));
  }, [number]);

  if (err) return <div className="p-12 text-center text-foreground/70">{err}</div>;
  if (!reg) return <div className="p-12 text-foreground/60">Chargement…</div>;

  const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

  const downloadBadge = () => {
    // generate printable HTML in a new window
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Badge ${reg.registration_number}</title>
      <style>
        body{margin:0;font-family:'Helvetica',sans-serif;background:#F9FAFB;display:grid;place-items:center;min-height:100vh;padding:20px;}
        .badge{background:#fff;width:380px;border-radius:6px;overflow:hidden;box-shadow:0 6px 32px rgba(0,0,0,.12);border:1px solid #e5e7eb}
        .top{background:#0A2540;color:#fff;padding:24px;text-align:center}
        .top h1{margin:0;font-size:18px;letter-spacing:.2em;text-transform:uppercase;font-weight:700;color:#D4AF37}
        .top h2{margin:6px 0 0;font-size:14px;font-weight:400;opacity:.9}
        .body{padding:24px;text-align:center}
        .name{font-size:22px;font-weight:700;color:#0A2540;margin:0 0 6px}
        .meta{color:#6b7280;font-size:13px;margin:0 0 16px}
        img{width:200px;height:200px;margin:8px auto;display:block}
        .num{margin-top:14px;font-family:monospace;background:#F9FAFB;border:1px dashed #D4AF37;padding:8px;font-size:13px;letter-spacing:.1em}
        .ev{background:#F9FAFB;padding:14px 24px;font-size:12px;color:#374151;border-top:1px solid #e5e7eb}
        @media print { body{background:#fff} }
      </style>
      </head><body><div class='badge'>
        <div class='top'><h1>Front Uni RDC</h1><h2>Badge officiel</h2></div>
        <div class='body'>
          <div class='name'>${reg.first_name} ${reg.last_name}</div>
          <div class='meta'>${reg.city}, ${reg.country}</div>
          <img src='${reg.qr_data_url}' alt='QR' />
          <div class='num'>${reg.registration_number}</div>
        </div>
        <div class='ev'><strong>${reg.event_title}</strong></div>
      </div>
      <script>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
  };

  const share = () => {
    const txt = `Je suis inscrit·e à « ${reg.event_title} » avec le Front Uni pour la RDC.`;
    if (navigator.share) navigator.share({ title: "Inscription confirmée", text: txt }).catch(() => {});
    else { navigator.clipboard.writeText(txt); toast.success("Texte copié dans le presse-papiers"); }
  };

  return (
    <div className="bg-bone min-h-screen py-16" data-testid="registration-success-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <div className="text-center">
          <Logo className="w-20 h-20 mx-auto" />
          <CheckCircle2 className="w-12 h-12 text-gold mx-auto mt-4" />
          <div className="eyebrow text-gold-dark mt-6">Inscription confirmée</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-ink mt-4 leading-tight">Bienvenue, {reg.first_name}.</h1>
          <p className="mt-4 text-foreground/70 max-w-xl mx-auto">Votre inscription à « {reg.event_title} » est enregistrée. Conservez votre badge numérique et votre numéro d'inscription.</p>
        </div>

        <div className="mt-12 bg-white border border-border rounded-sm overflow-hidden shadow-lg">
          <div className="bg-ink text-white px-8 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo className="w-10 h-10 shrink-0" />
              <div>
                <div className="eyebrow eyebrow-on-dark text-[0.65rem]">Badge officiel</div>
                <div className="font-serif text-xl mt-1">Front Uni pour la RDC</div>
              </div>
            </div>
            <div className="w-12 h-12 grid place-items-center bg-gold text-ink rounded-sm font-serif text-xl">F</div>
          </div>
          <div className="p-8 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="text-2xl font-serif text-ink leading-tight">{reg.first_name} {reg.last_name}</div>
              <div className="mt-1 text-foreground/60 text-sm">{reg.email}</div>
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-foreground/70"><MapPin className="w-4 h-4 text-gold-dark" /> {reg.city}, {reg.country}</div>
                <div className="flex items-center gap-2 text-foreground/70"><Calendar className="w-4 h-4 text-gold-dark" /> {reg.event_title}</div>
              </div>
              <div className="mt-6 inline-block bg-bone border border-dashed border-gold text-ink font-mono text-sm px-4 py-2 rounded-sm tracking-wider">
                {reg.registration_number}
              </div>
            </div>
            <div className="md:col-span-2 grid place-items-center">
              <img src={reg.qr_data_url} alt="QR code badge" className="w-44 h-44" data-testid="badge-qr" />
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={downloadBadge} className="btn-primary" data-testid="download-badge">
            <Download className="w-4 h-4" /> Télécharger / imprimer le badge
          </button>
          <button onClick={share} className="btn-outline" data-testid="share-badge">
            <Share2 className="w-4 h-4" /> Partager
          </button>
          <Link to="/" className="btn-outline">Retour à l'accueil</Link>
        </div>
      </div>
    </div>
  );
}
